import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { loadLocaleModules } from '@/lib/locale-modules';

// Load only the active language. English is dynamic too: a Polish user should
// not download the complete English catalog in addition to Polish merely
// because English is the fallback language.
const allModules = import.meta.glob('@/locales/*/*.json');

// Locale codes that actually ship a bundle — the single source of truth for
// which languages the client can render. Used to validate a user's stored
// language before switching to it: a since-removed locale (e.g. a user still
// carrying 'ar' after it was dropped) has no bundle, so switching into it would
// leave text in the English fallback while skewing Intl date/number formatting
// to the wrong locale. Derived from the glob so it can never drift from disk.
export const availableLanguages = new Set(
  Object.keys(allModules).map((path) => path.split('/').at(-2)),
);

function deepMerge(target, source) {
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === 'object' &&
      !Array.isArray(target[key])
    ) {
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
}

i18n.use(initReactI18next).init({
  resources: {},
  lng: 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});

// Cache of loaded languages
const loadedLanguages = new Set();

/**
 * Load a language's translations on demand. Returns a promise that resolves
 * when the language bundle is ready.
 */
export async function loadLanguage(lang) {
  if (loadedLanguages.has(lang)) return;

  const results = await loadLocaleModules(lang, allModules);
  const merged = {};
  for (const data of results) {
    deepMerge(merged, data);
  }

  i18n.addResourceBundle(lang, 'translation', merged, true, true);
  loadedLanguages.add(lang);
}

export default i18n;
